import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress
} from '@material-ui/core';
import ActionIconButton from '../../../../components/atom/ActionIconButton';

import { MODAL_TYPE, useModals } from '../../../../hooks/useModal';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import AdminHeader from '../../../../components/atom/AdminHeader';
import TablePager from '../../../../components/atom/TablePager';
import { DeleteIcon, EditIcon } from '../../../../icons';

function UserGroupListPage({ clientId }) {
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();

    const [data, loading, error, setData, reloadUserGroups] = useSwaggerClient(
        'usergroups',
        'listUserGroupsPerClient',
        {
            clientId,
            page,
            pageSize,
            search
        },
        [page, pageSize, search]
    );
    // wire error to a notification
    useEffect(() => {
        if (error) {
            notifyError(error.message);
        }
    }, [error, notifyError]);

    const deleteUserGroup = useCallback(
        userGroupId => {
            doSwaggerCall('usergroups', 'deleteUserGroupById', { userGroupId })
                .then(() => {
                    // we finished, remove userGroup from list
                    notifySuccess('UserGroup deleted');
                    reloadUserGroups();
                })
                .catch(() => {
                    notifyError('Cannot delete user group');
                });
        },
        [setData, data, notifyError, notifySuccess, reloadUserGroups]
    );

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <AdminHeader
                title="User Groups"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
                createLabel="New user group"
                onCreate={() => {
                    showModal(MODAL_TYPE.CREATE_USERGROUP, {
                        clientId
                    });
                }}
            />
            <Grid item container xs={12} justifyContent="center">
                {loading && data?.userGroups.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <Table style={{ backgroundColor: 'white' }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data &&
                                data.userGroups &&
                                data.userGroups.map(userGroup => (
                                    <TableRow key={userGroup.id}>
                                        <TableCell>{userGroup.name}</TableCell>
                                        <TableCell style={{ width: 100 }}>
                                            <ActionIconButton
                                                title="Edit"
                                                icon={EditIcon}
                                                to={`/admin/client/${clientId}/usergroups/${userGroup.id}`}
                                            />
                                            <ActionIconButton
                                                title="Delete"
                                                icon={DeleteIcon}
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: `Are you sure you want to remove ${userGroup?.name}?`,
                                                        content: 'This action is irreversible.',
                                                        confirm: () => deleteUserGroup(userGroup.id)
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={data?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default UserGroupListPage;
